import { CommentsField } from "@/shared/components/CommentsField";
import {
	HoursMinutesTextField,
	toHHMM,
	toHumanDuration,
} from "@/shared/components/HoursMinutesTextField";
import { NumericTextField } from "@/shared/components/NumericTextField";
import { PercentField } from "@/shared/components/PercentField";
import { SectionSelector } from "@/shared/selectors";
import { BilletStatusSelector } from "@/shared/selectors/BilletStatusSelector";
import {
	EventTypeSelector,
	productionTypes,
} from "@/shared/selectors/EventTypeSelector";
import { InquiryStatusSelector } from "@/shared/selectors/InquiryStatusSelector";
import {
	ButtonWithLoading,
	ErrorModal,
	getMomentTz,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import { Calculate, ExpandMore } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid2,
	IconButton,
	InputAdornment,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { useMutation } from "@tanstack/react-query";
import { round } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { calculateDeadline } from "../../hooks/useEventHandlers";
import { useRollChangeStats } from "../../hooks/useSearchEvents";
import { Event, EVENT_INITIAL_VALUES } from "../../models/Event";
import {
	upsertEvent,
	upsertProductionEvent,
} from "../../repositories/CalendarRepository";
import {
	calculateFinishedYield,
	calculateMillYield,
	calculateNewEnd,
	calculateRollingHours,
	prorateScheduledByMonth,
} from "../../utils/calculateFields";

type SectionValue = {
	id: number | null;
	name?: string | null;
	tph?: number | null;
	targetTPH?: number | null;
	millYield?: number | null;
	finishedYield?: number | null;
};

const moment = getMomentTz();

export const NewEventModal = ({
	open,
	onHide,
	newEventDates,
	subtitle,
	isDraftMode,
	restrictToEventType,
}: {
	open: boolean;
	onHide: (success: boolean) => void;
	newEventDates: {
		start: Date;
		end: Date | null;
	} | null;
	subtitle?: string;
	isDraftMode: boolean;
	restrictToEventType?: string;
}) => {
	const defaultAreaId = useCalendarAreaId();

	const [error, setError] = useState("");
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const [expandedAccordion, setExpandedAccordion] = useState<
		("scheduled" | "actual")[]
	>([]);
	const [sectionValues, setSectionValues] = useState<SectionValue>({
		id: null,
	});
	const [fieldsPrefilled, setFieldsPrefilled] = useState<
		Partial<
			Record<keyof Event, "prefilled" | "autocalculated" | "warning" | boolean>
		>
	>({
		End: false,
		ScheduledChargedTons: false,
		ScheduledRolledTons: false,
		ScheduledFinishedTons: false,
		BudgetTPH: false,
		ScheduledRollingHours: false,
		ScheduledRollChangeHours: false,
		ScheduledMillYield: false,
		ScheduledFinishedYield: false,
	});
	const [sectionDefaultsDiffs, setSectionDefaultsDiffs] = useState<
		{ field: string; from: string; to: string }[]
	>([]);

	const { control, reset, watch, setValue, getValues, trigger } =
		useForm<Event>({
			defaultValues: EVENT_INITIAL_VALUES,
			mode: "all",
		});

	const selectedType = watch("EventTypeCode");

	const startDate = watch("Start");
	const selectedSectionId = watch("SectionId");
	const endDate = watch("End");

	const newEventMonths = (() => {
		if (!startDate || !endDate) return [];
		const m1 = moment(startDate).startOf("month");
		const m2 = moment(endDate).startOf("month");
		if (m2.isSame(m1, "month")) return [m1.toDate()];
		return [m1.toDate(), m2.toDate()];
	})();
	const isNewEventMultiMonth = newEventMonths.length > 1;

	const [firstMonthScheduled, setFirstMonthScheduled] = useState<{
		ScheduledChargedTons: number | null;
		ScheduledRolledTons: number | null;
		ScheduledFinishedTons: number | null;
		ScheduledRollingHours: number | null;
	}>({
		ScheduledChargedTons: null,
		ScheduledRolledTons: null,
		ScheduledFinishedTons: null,
		ScheduledRollingHours: null,
	});

	const [secondMonthScheduled, setSecondMonthScheduled] = useState<{
		ScheduledChargedTons: number | null;
		ScheduledRolledTons: number | null;
		ScheduledFinishedTons: number | null;
		ScheduledRollingHours: number | null;
	}>({
		ScheduledChargedTons: null,
		ScheduledRolledTons: null,
		ScheduledFinishedTons: null,
		ScheduledRollingHours: null,
	});

	const scheduledChargedTons = watch("ScheduledChargedTons");
	const scheduledRolledTons = watch("ScheduledRolledTons");
	const scheduledFinishedTons = watch("ScheduledFinishedTons");
	const scheduledRollingHours = watch("ScheduledRollingHours");

	useEffect(() => {
		if (isNewEventMultiMonth && startDate && endDate) {
			const [m1, m2] = prorateScheduledByMonth(
				startDate,
				endDate,
				newEventMonths[1],
				{
					ScheduledChargedTons: scheduledChargedTons ?? null,
					ScheduledRolledTons: scheduledRolledTons ?? null,
					ScheduledFinishedTons: scheduledFinishedTons ?? null,
					ScheduledRollingHours: scheduledRollingHours ?? null,
				},
			);
			setFirstMonthScheduled(m1);
			setSecondMonthScheduled(m2);
		} else {
			setFirstMonthScheduled({
				ScheduledChargedTons: null,
				ScheduledRolledTons: null,
				ScheduledFinishedTons: null,
				ScheduledRollingHours: null,
			});
			setSecondMonthScheduled({
				ScheduledChargedTons: null,
				ScheduledRolledTons: null,
				ScheduledFinishedTons: null,
				ScheduledRollingHours: null,
			});
		}
	}, [
		isNewEventMultiMonth,
		startDate,
		endDate,
		scheduledChargedTons,
		scheduledRolledTons,
		scheduledFinishedTons,
		scheduledRollingHours,
	]);

	const { data: rollChangeStats } = useRollChangeStats({
		sectionId: selectedSectionId,
		start: startDate ?? null,
		enabled: open && selectedType === "P",
	});

	const deadline = watch("Deadline");
	const inquiryCloseDate = watch("InquiryCloseDate");

	const deadlineStatus = () => {
		if (!deadline || !endDate || !startDate) return null;

		const deadlineTime = new Date(deadline).getTime();
		const startTime = new Date(startDate).getTime();
		const endTime = new Date(endDate).getTime();

		if (endTime < deadlineTime)
			return { type: "valid", text: "✓ Before deadline" } as const;
		if (deadlineTime > startTime && deadlineTime < endTime)
			return { type: "warning", text: "⚠ Finishes after deadline" } as const;
		return { type: "error", text: "✕ Starts after deadline" } as const;
	};

	const inquiryCloseDateStatus = () => {
		if (!inquiryCloseDate || !endDate || !startDate) return null;

		const inquiryCloseDateTime = new Date(inquiryCloseDate).getTime();
		const startTime = new Date(startDate).getTime();
		const endTime = new Date(endDate).getTime();

		if (inquiryCloseDateTime >= startTime && inquiryCloseDateTime <= endTime)
			return null;

		if (inquiryCloseDateTime < startTime) return null;
		return "✕ Can't be after event end";
	};

	const resetFieldsPrefilled = () => {
		setFieldsPrefilled({
			ScheduledChargedTons: false,
			ScheduledRolledTons: false,
			ScheduledFinishedTons: false,
			BudgetTPH: false,
			ScheduledRollingHours: false,
			ScheduledRollChangeHours: false,
			ScheduledMillYield: false,
			ScheduledFinishedYield: false,
		});
	};

	const handleClose = (success: boolean) => {
		setExpandedAccordion([]);
		resetFieldsPrefilled();
		onHide(success);
	};

	const newEvent = useMutation(upsertEvent, {
		onSuccess: () => {
			handleClose(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});
	const newProductionEvent = useMutation(upsertProductionEvent, {
		onSuccess: () => {
			handleClose(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit = async (data: Event, updateSectionDefaults?: boolean) => {
		const valid = await trigger();
		if (!valid) return;

		if (data.EventTypeCode === "P") {
			const sectionDiffers =
				data.BudgetTPH !== sectionValues.tph ||
				data.ScheduledMillYield !== sectionValues.millYield ||
				data.ScheduledFinishedYield !== sectionValues.finishedYield ||
				data.TargetTPH !== sectionValues.targetTPH;

			if (sectionDiffers && updateSectionDefaults === undefined) {
				const diffs: { field: string; from: string; to: string }[] = [];
				if (data.BudgetTPH !== sectionValues.tph)
					diffs.push({
						field: "TPH",
						from: `${sectionValues.tph ?? "-"}`,
						to: `${data.BudgetTPH ?? "-"}`,
					});
				if (data.ScheduledMillYield !== sectionValues.millYield)
					diffs.push({
						field: "Mill Yield",
						from: `${sectionValues.millYield ?? "-"}%`,
						to: `${data.ScheduledMillYield ?? "-"}%`,
					});
				if (data.ScheduledFinishedYield !== sectionValues.finishedYield)
					diffs.push({
						field: "Finished Yield",
						from: `${sectionValues.finishedYield ?? "-"}%`,
						to: `${data.ScheduledFinishedYield ?? "-"}%`,
					});
				if (data.TargetTPH !== sectionValues.targetTPH)
					diffs.push({
						field: "Target TPH",
						from: `${sectionValues.targetTPH ?? "-"}`,
						to: `${data.TargetTPH ?? "-"}`,
					});
				setSectionDefaultsDiffs(diffs);
				return;
			}

			newProductionEvent.mutate({
				eventId: null,
				start: data.Start!,
				end: data.End ?? null,
				sectionId: data.SectionId,
				statusCode: data.StatusCode,
				inquiryStatusCode: data.InquiryStatusCode,
				inquiryCloseDate: data.InquiryCloseDate,
				billetsStatusCode: data.BilletsStatusCode,
				deadline: data.Deadline,
				targetTPH: data.TargetTPH,
				updateSectionDefaults,
				scheduledChargedTons: data.ScheduledChargedTons,
				scheduledRolledTons: data.ScheduledRolledTons,
				scheduledFinishedTons: data.ScheduledFinishedTons,
				budgetTPH: data.BudgetTPH,
				scheduledRollingHours: data.ScheduledRollingHours,
				scheduledRollChangeHours: data.ScheduledRollChangeHours,
				scheduledMillYield: data.ScheduledMillYield,
				scheduledFinishedYield: data.ScheduledFinishedYield,
				actualChargedTons: data.ActualChargedTons,
				actualRolledTons: data.ActualRolledTons,
				actualFinishedTons: data.ActualFinishedTons,
				actualTPH: data.ActualTPH,
				actualRollingHours: data.ActualRollingHours,
				actualRollChangeHours: data.ActualRollChangeHours,
				actualMillYield: data.ActualMillYield,
				actualFinishedYield: data.ActualFinishedYield,
				comments: data.EventComments,
				isDraftMode,
				defaultAreaId,
			});
		} else {
			newEvent.mutate({
				eventId: null,
				start: data.Start!,
				end: data.End ?? null,
				eventTypeCode: data.EventTypeCode!,
				comments: data.EventComments,
				isDraftMode,
				defaultAreaId,
			});
		}
	};

	const checkValueAndPrefill = async ({
		actualField,
		newValue,
		filledType = "prefilled",
		respectActualValue = true,
	}: {
		actualField: keyof Event;
		newValue: number | null;
		filledType?: "prefilled" | "autocalculated" | boolean;
		respectActualValue?: boolean;
	}) => {
		const actualValue = watch(actualField);

		if (!actualValue || !respectActualValue) {
			setValue(actualField, newValue ?? null);

			setFieldsPrefilled((prev) => ({
				...prev,
				[actualField]: filledType,
			}));
		}

		if (!respectActualValue) {
			await trigger();
		}
	};

	const calculateValues = ({
		finishedTons,
		millYield,
		finishedYield,
		tph,
		respectActualValue = true,
	}: {
		finishedTons: number;
		millYield?: number;
		finishedYield?: number;
		tph?: number;
		respectActualValue?: boolean;
	}) => {
		if (
			(getValues("ScheduledMillYield") || millYield) &&
			(getValues("ScheduledFinishedYield") || finishedYield) &&
			(getValues("BudgetTPH") || tph)
		) {
			const chargeTons =
				finishedTons /
				((getValues("ScheduledFinishedYield") || finishedYield!) / 100);
			const rolledTons =
				chargeTons * ((getValues("ScheduledMillYield") || millYield!) / 100);
			const estimatedRollingHs = rolledTons / (getValues("BudgetTPH") || tph!);

			checkValueAndPrefill({
				actualField: "ScheduledRollingHours",
				newValue: round(estimatedRollingHs),
				filledType: "autocalculated",
				respectActualValue,
			});
			checkValueAndPrefill({
				actualField: "ScheduledRolledTons",
				newValue: round(rolledTons),
				filledType: "autocalculated",
				respectActualValue,
			});
			checkValueAndPrefill({
				actualField: "ScheduledChargedTons",
				newValue: round(chargeTons),
				filledType: "autocalculated",
				respectActualValue,
			});

			calculateNewEnd({
				startDate,
				estimatedRollingHs,
				setValue,
				setFieldsPrefilled,
			});
		}
	};

	const autocalculateScheduledValues = ({
		finishedTons,
	}: {
		finishedTons: number;
	}) => {
		const mill = getValues("ScheduledMillYield");
		const finished = getValues("ScheduledFinishedYield");
		const tph = getValues("BudgetTPH");

		if (!mill || !finished || !tph) return;

		// Calculate the full chain
		const chargedTons = round(finishedTons / (finished / 100));
		const rolledTons = round(chargedTons * (mill / 100));
		const rollingHours = round(rolledTons / tph);

		// Update all values in sequence
		checkValueAndPrefill({
			actualField: "ScheduledChargedTons",
			newValue: chargedTons,
			filledType: "autocalculated",
			respectActualValue: false,
		});

		checkValueAndPrefill({
			actualField: "ScheduledRolledTons",
			newValue: rolledTons,
			filledType: "autocalculated",
			respectActualValue: false,
		});

		checkValueAndPrefill({
			actualField: "ScheduledRollingHours",
			newValue: rollingHours,
			filledType: "autocalculated",
			respectActualValue: false,
		});

		calculateNewEnd({
			startDate,
			estimatedRollingHs: rollingHours,
			setValue,
			setFieldsPrefilled,
		});
	};

	const handleBudgetTPHCascade = (value: number | null) => {
		// Update Budget TPH
		checkValueAndPrefill({
			actualField: "BudgetTPH",
			newValue: value,
			filledType: "prefilled",
			respectActualValue: false,
		});

		const rolledTons = getValues("ScheduledRolledTons") ?? 0;
		const newRollingHs = round(rolledTons / (value ?? 1));

		checkValueAndPrefill({
			actualField: "ScheduledRollingHours",
			newValue: newRollingHs,
			filledType: "autocalculated",
			respectActualValue: false,
		});

		calculateNewEnd({
			startDate,
			estimatedRollingHs: newRollingHs,
			setValue,
			setFieldsPrefilled,
		});
	};

	const handleScheduledYieldCascade = ({
		fieldName,
		value,
		isMillYield,
	}: {
		fieldName: "ScheduledMillYield" | "ScheduledFinishedYield";
		value: number | null;
		isMillYield: boolean;
	}) => {
		checkValueAndPrefill({
			actualField: fieldName,
			newValue: value,
			filledType: "prefilled",
			respectActualValue: false,
		});

		if (isMillYield) {
			// Mill Yield change: affects Rolled Tons → Rolling Hours → End
			const chargedTons = getValues("ScheduledChargedTons") ?? 0;
			const newRolledTons = round(chargedTons * ((value ?? 0) / 100));
			const tph = getValues("BudgetTPH") ?? 1;
			const newRollingHs = round(newRolledTons / tph);

			checkValueAndPrefill({
				actualField: "ScheduledRolledTons",
				newValue: newRolledTons,
				filledType: "autocalculated",
				respectActualValue: false,
			});

			checkValueAndPrefill({
				actualField: "ScheduledRollingHours",
				newValue: newRollingHs,
				filledType: "autocalculated",
				respectActualValue: false,
			});

			calculateNewEnd({
				startDate,
				estimatedRollingHs: newRollingHs,
				setValue,
				setFieldsPrefilled,
			});
		} else {
			// Finished Yield change: affects Charged Tons → Rolled Tons → Rolling Hours → End
			const finishedTons = getValues("ScheduledFinishedTons") ?? 0;
			autocalculateScheduledValues({
				finishedTons,
			});
		}
	};

	const updateYieldsBasedOnTons = ({
		yieldUpdates,
		userChanged = false,
	}: {
		yieldUpdates: Array<{
			field: "ScheduledMillYield" | "ScheduledFinishedYield";
			value: number | null;
		}>;
		userChanged?: boolean;
	}) => {
		yieldUpdates.forEach(({ field, value }) => {
			checkValueAndPrefill({
				actualField: field,
				newValue: value,
				filledType: userChanged ? "autocalculated" : "prefilled",
				respectActualValue: false,
			});
		});
	};

	const updateTonsAndRollingHours = ({
		chargedTons,
		rolledTons,
		rollingHours,
	}: {
		chargedTons?: number | null;
		rolledTons?: number | null;
		rollingHours?: number | null;
	}) => {
		if (chargedTons !== undefined) {
			checkValueAndPrefill({
				actualField: "ScheduledChargedTons",
				newValue: chargedTons,
				filledType: "autocalculated",
				respectActualValue: false,
			});
		}

		if (rolledTons !== undefined) {
			checkValueAndPrefill({
				actualField: "ScheduledRolledTons",
				newValue: rolledTons,
				filledType: "autocalculated",
				respectActualValue: false,
			});
		}

		if (rollingHours !== undefined && rollingHours !== null) {
			checkValueAndPrefill({
				actualField: "ScheduledRollingHours",
				newValue: rollingHours,
				filledType: "autocalculated",
				respectActualValue: false,
			});

			calculateNewEnd({
				startDate,
				estimatedRollingHs: rollingHours,
				setValue,
				setFieldsPrefilled,
			});
		}
	};

	useEffect(() => {
		if (!open || !rollChangeStats) return;
		const autoFillValue =
			rollChangeStats.LastScheduled ?? rollChangeStats.LastActual;
		if (autoFillValue == null || autoFillValue === 0) return;
		const currentValue = getValues("ScheduledRollChangeHours");
		const wasAutoFilled =
			fieldsPrefilled.ScheduledRollChangeHours === "autocalculated";
		// Only auto-fill if field is empty OR was previously auto-filled (not manually edited)
		if (currentValue != null && !wasAutoFilled) return;
		setValue("ScheduledRollChangeHours", autoFillValue);
		setFieldsPrefilled((prev) => ({
			...prev,
			ScheduledRollChangeHours: "autocalculated",
		}));
		calculateNewEnd({
			startDate,
			estimatedRollingHs: getValues("ScheduledRollingHours") ?? 0,
			setValue,
			setFieldsPrefilled,
		});
	}, [rollChangeStats, open]);

	useEffect(() => {
		if (newEventDates) {
			reset({
				...EVENT_INITIAL_VALUES,
				Start: newEventDates.start,
				End: newEventDates.end,
				EventTypeCode: restrictToEventType ?? "P",
			});
		}
	}, [newEventDates, restrictToEventType]);

	const autocalculateUpdated = () => {
		const sameMillYield =
			sectionValues.millYield === getValues("ScheduledMillYield");
		const sameFinishedYield =
			sectionValues.finishedYield === getValues("ScheduledFinishedYield");
		const sameChargedTons =
			round(getValues("ScheduledChargedTons") ?? 0) ===
			round(
				(getValues("ScheduledFinishedTons") ?? 0) /
					((sectionValues.finishedYield ?? 1) / 100),
			);
		const sameRolledTons =
			round(getValues("ScheduledRolledTons") ?? 0) ===
			round(
				(getValues("ScheduledChargedTons") ?? 0) *
					((sectionValues.millYield ?? 1) / 100),
			);
		const sameRollingHs =
			round(getValues("ScheduledRollingHours") ?? 0) ===
			round(
				(getValues("ScheduledRolledTons") ?? 0) / (getValues("BudgetTPH") ?? 1),
			);

		return (
			sameMillYield &&
			sameFinishedYield &&
			sameChargedTons &&
			sameRolledTons &&
			sameRollingHs
		);
	};

	const isAutoCalculated = autocalculateUpdated();

	return (
		<>
			<MesfModal
				title={"New Event"}
				subtitle={subtitle}
				open={open}
				id="new-event-modal"
				maxWidth="lg"
				handleClose={() => {
					handleClose(false);
				}}
			>
				<MesfModal.Content
					dividers
					sx={{
						backgroundColor: "#f6f6f6ff",
						overflow: "auto",
					}}
				>
					<Grid2 container spacing={1} style={{ alignItems: "center" }}>
						{newEventDates && (
							<>
								<Card
									sx={{
										width: "100%",
									}}
								>
									<CardContent>
										<Grid2
											container
											size={12}
											spacing={2}
											style={{ alignItems: "flex-start" }}
										>
											<Grid2 size={{ xs: 12, md: 4 }}>
												<Controller
													name="Start"
													control={control}
													rules={{
														required: "Start is required",
														deps: ["End"],
														validate: {
															notAfterEnd: (value) => {
																if (
																	value &&
																	getValues("End") &&
																	value > getValues("End")!
																) {
																	return "Start cannot be after End date.";
																}
																return true;
															},
														},
													}}
													render={({ field, fieldState: { error } }) => (
														<DateTimePicker
															label="Start"
															timezone={TimeService.getInstance().getServerTimeZone()}
															format="MM/DD/YY HH:mm"
															ampm={false}
															value={field.value ? moment(field.value) : null}
															onChange={async (value) => {
																const newStartDate = value?.toDate() ?? null;

																if (
																	selectedType === "P" &&
																	newStartDate &&
																	startDate &&
																	endDate
																) {
																	const currentStart = moment(startDate);
																	const currentEnd = moment(endDate);
																	const duration =
																		currentEnd.diff(currentStart);

																	const newEndDate = moment(newStartDate)
																		.add(duration, "ms")
																		.toDate();

																	setValue("End", newEndDate, {
																		shouldValidate: false,
																	});
																	setValue("Start", newStartDate, {
																		shouldValidate: true,
																	});
																} else {
																	setValue("Start", newStartDate!, {
																		shouldValidate: true,
																	});
																}
															}}
															maxDate={moment(endDate)}
															slotProps={{
																textField: {
																	size: "small",
																	fullWidth: true,
																	margin: "none",
																	variant: "outlined",
																	error: !!error,
																	helperText: error?.message,
																},
															}}
														/>
													)}
												/>
											</Grid2>
											<Grid2 size={{ xs: 12, md: 4 }}>
												<Controller
													name="End"
													control={control}
													rules={{
														deps: ["Start"],
														validate: {
															notBeforeStart: (value) => {
																if (
																	value &&
																	getValues("Start") &&
																	value < getValues("Start")
																) {
																	return "End cannot be before Start.";
																}
																return true;
															},
															notSameAsStart: (value) => {
																if (
																	value &&
																	startDate &&
																	moment(value).isSame(moment(startDate))
																) {
																	return "End cannot be the same as Start.";
																}
																return true;
															},
														},
													}}
													render={({ field, fieldState: { error } }) => (
														<Tooltip
															title={
																fieldsPrefilled.End
																	? "Autocalculated (Start + Rolling Hours)"
																	: ""
															}
														>
															<Box component="span">
																<DateTimePicker
																	label="End"
																	timezone={TimeService.getInstance().getServerTimeZone()}
																	format="MM/DD/YY HH:mm"
																	ampm={false}
																	value={
																		field.value ? moment(field.value) : null
																	}
																	onChange={(value) =>
																		field.onChange(value?.toDate() ?? null)
																	}
																	minDate={moment(startDate)}
																	disabled={getValues("EventTypeCode") === "P"}
																	slotProps={{
																		textField: {
																			size: "small",
																			fullWidth: true,
																			variant: "outlined",
																			margin: "none",
																			error: !!error,
																			helperText: error?.message
																				? error.message
																				: fieldsPrefilled.End &&
																						getValues("EventTypeCode") === "P"
																					? "(Start + Estim. Rolling Hs.)"
																					: "",
																			FormHelperTextProps: {
																				sx: {
																					color: error
																						? (theme) =>
																								theme.palette.error.main
																						: (theme) =>
																								`${theme.palette.primary.main} !important`,
																				},
																			},
																		},
																	}}
																/>
															</Box>
														</Tooltip>
													)}
												/>
											</Grid2>
											<Grid2 size={{ xs: 6, md: 4 }}>
												<Controller
													name="EventTypeCode"
													control={control}
													render={({ field, fieldState: { error } }) => (
														<EventTypeSelector
															value={field.value}
															onChange={(value: string) =>
																field.onChange(value)
															}
															fieldError={error}
															disabled={!!restrictToEventType}
														/>
													)}
												/>
											</Grid2>
											{selectedType === "P" ? (
												<>
													<Grid2 size={{ xs: 6, md: 4 }}>
														<Controller
															name="SectionId"
															control={control}
															rules={{
																required:
																	selectedType === "P"
																		? "Section is required"
																		: false,
															}}
															render={({ field, fieldState: { error } }) => (
																<SectionSelector
																	value={field.value}
																	onChange={(
																		newId: number | null,
																		name?: string | null,
																		tph?: number | null,
																		targetTPH?: number | null,
																		millYield?: number | null,
																		finishedYield?: number | null,
																	) => {
																		setSectionValues({
																			id: newId,
																			name,
																			tph,
																			targetTPH,
																			millYield,
																			finishedYield,
																		});
																		checkValueAndPrefill({
																			actualField: "TargetTPH",
																			newValue: targetTPH ?? null,
																			filledType: "prefilled",
																			respectActualValue: false,
																		});

																		checkValueAndPrefill({
																			actualField: "BudgetTPH",
																			newValue: tph ?? null,
																			filledType: "prefilled",
																		});
																		checkValueAndPrefill({
																			actualField: "ScheduledMillYield",
																			newValue: millYield ?? null,
																			filledType: "prefilled",
																		});
																		checkValueAndPrefill({
																			actualField: "ScheduledFinishedYield",
																			newValue: finishedYield ?? null,
																			filledType: "prefilled",
																		});
																		field.onChange(newId);
																		setExpandedAccordion(["scheduled"]);
																	}}
																	fieldError={error}
																/>
															)}
														/>
													</Grid2>
													<Grid2
														size={{
															xs: 6,
															md: 4,
														}}
													>
														<Controller
															name="BilletsStatusCode"
															control={control}
															render={({ field, fieldState: { error } }) => (
																<BilletStatusSelector
																	value={field.value ?? null}
																	onChange={(value: string) =>
																		field.onChange(value)
																	}
																	fieldError={error}
																/>
															)}
														/>
													</Grid2>
													<Grid2
														size={{
															xs: 6,
															md: 4,
														}}
													>
														<Controller
															name="StatusCode"
															control={control}
															render={({ field, fieldState: { error } }) => (
																<TextField
																	label="Status"
																	value={
																		productionTypes.find(
																			(t) => t.code === field.value,
																		)?.name ?? ""
																	}
																	disabled
																	margin="none"
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ xs: 6, md: 4 }}>
														<Controller
															name="Deadline"
															control={control}
															render={({ field, fieldState: { error } }) => (
																<Tooltip title={calculateDeadline(field.value)}>
																	<Box component="span">
																		<DateTimePicker
																			label="Rolling Deadline"
																			timezone={TimeService.getInstance().getServerTimeZone()}
																			format="MM/DD/YY HH:mm"
																			ampm={false}
																			value={
																				field.value ? moment(field.value) : null
																			}
																			onChange={field.onChange}
																			slotProps={{
																				textField: {
																					size: "small",
																					fullWidth: true,
																					margin: "none",
																					variant: "outlined",
																					error: !!error,
																					helperText:
																						deadlineStatus()?.text ?? null,
																					FormHelperTextProps: {
																						sx: {
																							color:
																								deadlineStatus()?.type ===
																								"valid"
																									? (theme) =>
																											`${theme.palette.success.main} !important`
																									: deadlineStatus()?.type ===
																											"warning"
																										? (theme) =>
																												`${theme.palette.warning.main} !important`
																										: (theme) =>
																												`${theme.palette.error.main} !important`,
																						},
																					},
																				},
																			}}
																		/>
																	</Box>
																</Tooltip>
															)}
														/>
													</Grid2>
													<Grid2
														size={{
															xs: 6,
															md: 4,
														}}
													>
														<Controller
															name="InquiryStatusCode"
															control={control}
															render={({ field, fieldState: { error } }) => (
																<InquiryStatusSelector
																	value={field.value}
																	onChange={(value: string) =>
																		field.onChange(value)
																	}
																	fieldError={error}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ xs: 6, md: 4 }}>
														<Controller
															name="InquiryCloseDate"
															control={control}
															render={({ field, fieldState: { error } }) => (
																<Tooltip
																	title="If this date is reached, the system must automatically switch the Inquiry Status to 'Closed'"
																	open={tooltipOpen && !datePickerOpen}
																	onOpen={() => setTooltipOpen(true)}
																	onClose={() => setTooltipOpen(false)}
																	enterDelay={300}
																	placement="right"
																>
																	<Box component="span">
																		<DatePicker
																			label="Inquiry Close Date"
																			timezone={TimeService.getInstance().getServerTimeZone()}
																			format="MM/DD/YY"
																			value={
																				field.value ? moment(field.value) : null
																			}
																			onChange={field.onChange}
																			onOpen={() => {
																				setDatePickerOpen(true);
																				setTooltipOpen(false);
																			}}
																			onClose={() => {
																				setDatePickerOpen(false);
																			}}
																			slotProps={{
																				textField: {
																					size: "small",
																					fullWidth: true,
																					margin: "none",
																					variant: "outlined",
																					error: !!error,
																					helperText:
																						inquiryCloseDateStatus() ?? null,
																					FormHelperTextProps: {
																						sx: {
																							color: inquiryCloseDateStatus()
																								? (theme) =>
																										`${theme.palette.error.main} !important`
																								: undefined,
																						},
																					},
																				},
																			}}
																		/>
																	</Box>
																</Tooltip>
															)}
														/>
													</Grid2>
												</>
											) : null}
										</Grid2>
									</CardContent>
								</Card>

								{selectedType === "P" ? (
									<>
										<Grid2 size={{ md: 12 }}>
											<Accordion
												expanded={expandedAccordion.includes("scheduled")}
												onChange={(e, expanded) => {
													const target = e.target as HTMLElement;
													if (
														target.closest(
															".MuiAccordionSummary-expandIconWrapper",
														)
													) {
														if (expanded) {
															setExpandedAccordion([
																...expandedAccordion,
																"scheduled",
															]);
														} else {
															setExpandedAccordion([
																...expandedAccordion.filter(
																	(e) => e !== "scheduled",
																),
															]);
														}
													}
												}}
												data-test-id="accordion-scheduled"
											>
												<AccordionSummary
													expandIcon={
														<Box display="flex" alignItems="center">
															<ExpandMore
																sx={{ cursor: "pointer !important" }}
															/>
															{expandedAccordion.includes("scheduled") ? (
																<Tooltip
																	title={`Autocalculate fields based on Scheduled Finished Tons, Section TPH, Section Mill Yield & Section Finished Yield`}
																>
																	<IconButton
																		size="small"
																		sx={{
																			cursor: isAutoCalculated
																				? "default !important"
																				: "auto",
																		}}
																		onClick={(e) => {
																			e.stopPropagation();

																			if (isAutoCalculated) return;

																			// Reset mill yield, finished yield, tph and hours
																			checkValueAndPrefill({
																				actualField: "ScheduledMillYield",
																				newValue:
																					sectionValues.millYield ?? null,
																				filledType: "prefilled",
																				respectActualValue: false,
																			});
																			checkValueAndPrefill({
																				actualField: "ScheduledFinishedYield",
																				newValue:
																					sectionValues.finishedYield ?? null,
																				filledType: "prefilled",
																				respectActualValue: false,
																			});
																			checkValueAndPrefill({
																				actualField: "BudgetTPH",
																				newValue: sectionValues.tph ?? null,
																				filledType: "prefilled",
																				respectActualValue: false,
																			});
																			checkValueAndPrefill({
																				actualField: "TargetTPH",
																				newValue:
																					sectionValues.targetTPH ?? null,
																				filledType: "prefilled",
																				respectActualValue: false,
																			});

																			const newChargeTons =
																				(getValues("ScheduledFinishedTons") ??
																					0) /
																				((sectionValues.finishedYield ?? 1) /
																					100);
																			const newRolledTons =
																				newChargeTons *
																				((sectionValues.millYield ?? 1) / 100);
																			const newRollingHs =
																				calculateRollingHours(
																					newRolledTons,
																					sectionValues.tph ?? 1,
																				);

																			checkValueAndPrefill({
																				actualField: "ScheduledRollingHours",
																				newValue: Number(newRollingHs),
																				filledType: "autocalculated",
																				respectActualValue: false,
																			});

																			checkValueAndPrefill({
																				actualField: "ScheduledChargedTons",
																				newValue: newChargeTons,
																				filledType: "autocalculated",
																				respectActualValue: false,
																			});
																			checkValueAndPrefill({
																				actualField: "ScheduledRolledTons",
																				newValue: newRolledTons,
																				filledType: "autocalculated",
																				respectActualValue: false,
																			});

																			calculateNewEnd({
																				startDate,
																				estimatedRollingHs:
																					Number(newRollingHs),
																				setValue,
																				setFieldsPrefilled,
																			});
																		}}
																	>
																		<Calculate
																			fontSize="small"
																			color={
																				isAutoCalculated
																					? "disabled"
																					: "primary"
																			}
																		/>
																	</IconButton>
																</Tooltip>
															) : null}
														</Box>
													}
													sx={{
														cursor: "default !important",
													}}
												>
													<Typography
														variant="h6"
														color="textSecondary"
														fontSize={14}
													>
														Scheduled
													</Typography>
												</AccordionSummary>
												<AccordionDetails>
													<Grid2 container spacing={2} width="100%" size={12}>
														<Grid2 size={{ md: 9.6 }}>
															<Grid2 container spacing={2}>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="ScheduledChargedTons"
																		control={control}
																		rules={{
																			deps: ["ScheduledRolledTons"],
																			validate: {
																				minValue: (value) => {
																					const rolledTons = getValues(
																						"ScheduledRolledTons",
																					);
																					if (
																						(value ?? 0) < (rolledTons ?? 0)
																					) {
																						return "Must be greater or equal than to Rolled Tons";
																					}
																					return true;
																				},
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<NumericTextField
																				label="Charged Tons"
																				value={field.value}
																				onChange={(value: number | null) => {
																					const differentValue =
																						field.value && field.value != value;
																					field.onChange(value);

																					const finishedTons = watch(
																						"ScheduledFinishedTons",
																					);
																					const rolledTons = watch(
																						"ScheduledRolledTons",
																					);

																					const newMillYield =
																						calculateMillYield(
																							value ?? 0,
																							rolledTons ?? 1,
																						);
																					const newFinishedYield =
																						calculateFinishedYield(
																							finishedTons ?? 0,
																							value ?? 1,
																						);

																					updateYieldsBasedOnTons({
																						yieldUpdates: [
																							{
																								field: "ScheduledMillYield",
																								value: newMillYield,
																							},
																							{
																								field: "ScheduledFinishedYield",
																								value: newFinishedYield,
																							},
																						],
																						userChanged:
																							!!differentValue ||
																							sectionValues.millYield !==
																								newMillYield ||
																							sectionValues.finishedYield !==
																								newFinishedYield,
																					});

																					const expectedValue = round(
																						(getValues(
																							"ScheduledFinishedTons",
																						) ?? 0) /
																							((getValues(
																								"ScheduledFinishedYield",
																							) ?? 1) /
																								100),
																					);

																					setFieldsPrefilled((prev) => ({
																						...prev,
																						ScheduledChargedTons: false,
																						ScheduledRolledTons: false,
																					}));
																				}}
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.ScheduledChargedTons
																							? "Autocalculated (Fin. Tons / Fin. Yield)"
																							: ""
																				}
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="ScheduledRolledTons"
																		control={control}
																		rules={{
																			deps: [
																				"ScheduledChargedTons",
																				"ScheduledFinishedTons",
																			],
																			validate: {
																				maxValue: (value) => {
																					const chargeTons = getValues(
																						"ScheduledChargedTons",
																					);
																					if (
																						(value ?? 0) > (chargeTons ?? 0)
																					) {
																						return "Must be less or equal than to Charge Tons";
																					}
																					return true;
																				},
																				minValue: (value) => {
																					const finishedTons = getValues(
																						"ScheduledFinishedTons",
																					);
																					if (
																						(value ?? 0) < (finishedTons ?? 0)
																					) {
																						return "Must be greater or equal than to Finished Tons";
																					}
																					return true;
																				},
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<NumericTextField
																				label="Rolled Tons"
																				value={field.value}
																				onChange={(value: number | null) => {
																					const differentValue =
																						field.value && field.value != value;
																					field.onChange(value);

																					const chargeTons = watch(
																						"ScheduledChargedTons",
																					);
																					const tonsPerHour =
																						watch("BudgetTPH");

																					const newMillYield =
																						calculateMillYield(
																							chargeTons ?? 0,
																							value ?? 1,
																						);
																					const newRollingHs =
																						calculateRollingHours(
																							value ?? 0,
																							tonsPerHour ?? 1,
																						);

																					updateYieldsBasedOnTons({
																						yieldUpdates: [
																							{
																								field: "ScheduledMillYield",
																								value: newMillYield,
																							},
																						],
																						userChanged:
																							!!differentValue ||
																							sectionValues.millYield !==
																								newMillYield,
																					});

																					updateTonsAndRollingHours({
																						rollingHours: Number(newRollingHs),
																					});

																					const expectedValue = round(
																						(getValues(
																							"ScheduledChargedTons",
																						) ?? 0) *
																							((getValues(
																								"ScheduledMillYield",
																							) ?? 1) /
																								100),
																					);

																					setFieldsPrefilled((prev) => ({
																						...prev,
																						ScheduledRolledTons: false,
																					}));
																				}}
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.ScheduledRolledTons
																							? "Autocalculated (Charge Tons * Mill Yield)"
																							: ""
																				}
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="ScheduledFinishedTons"
																		control={control}
																		rules={{
																			deps: ["ScheduledRolledTons"],
																			validate: {
																				maxValue: (value) => {
																					const rolledTons = getValues(
																						"ScheduledRolledTons",
																					);
																					if (
																						(value ?? 0) > (rolledTons ?? 0)
																					) {
																						return "Must be less or equal than to Rolled Tons";
																					}
																					return true;
																				},
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<NumericTextField
																				label="Finished Tons"
																				value={field.value}
																				onChange={(value: number | null) => {
																					const differentValue =
																						field.value && field.value != value;
																					field.onChange(value);

																					const chargeTons = watch(
																						"ScheduledChargedTons",
																					);
																					const newFinishedYield =
																						calculateFinishedYield(
																							value ?? 0,
																							chargeTons ?? 1,
																						);

																					updateYieldsBasedOnTons({
																						yieldUpdates: [
																							{
																								field: "ScheduledFinishedYield",
																								value: newFinishedYield,
																							},
																						],
																						userChanged:
																							!!differentValue ||
																							sectionValues.finishedYield !==
																								newFinishedYield,
																					});

																					setFieldsPrefilled((prev) => ({
																						...prev,
																						ScheduledFinishedTons: false,
																					}));
																				}}
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.ScheduledFinishedTons
																							? ""
																							: ""
																				}
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="ScheduledRollingHours"
																		control={control}
																		render={({ field }) => (
																			<NumericTextField
																				label="Rolling Hours"
																				value={field.value}
																				onChange={(value) => {
																					field.onChange(value);

																					const rolledTons = watch(
																						"ScheduledRolledTons",
																					);

																					const newTPH =
																						(rolledTons ?? 0) / (value ?? 1);

																					checkValueAndPrefill({
																						actualField: "BudgetTPH",
																						newValue: round(Number(newTPH)),
																						filledType: "autocalculated",
																						respectActualValue: false,
																					});

																					calculateNewEnd({
																						startDate,
																						estimatedRollingHs: value ?? 0,
																						setValue,
																						setFieldsPrefilled,
																					});

																					setFieldsPrefilled((prev) => ({
																						...prev,
																						ScheduledRollingHours: false,
																					}));
																				}}
																				onBlur={field.onBlur}
																				helperText={
																					fieldsPrefilled.ScheduledRollingHours
																						? "Autocalculated (Rolled Tons / TPH)"
																						: ""
																				}
																				InputProps={{
																					endAdornment: (
																						<InputAdornment position="end">
																							hs
																						</InputAdornment>
																					),
																				}}
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="BudgetTPH"
																		control={control}
																		rules={{
																			deps: ["TargetTPH"],
																			validate: (value) => {
																				const targetTPH =
																					getValues("TargetTPH");
																				if (!value || !targetTPH) return;

																				if (value > targetTPH) {
																					return "Budget TPH cannot be higher than Target TPH";
																				}

																				return true;
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<NumericTextField
																				label="Budget TPH"
																				value={field.value}
																				onChange={(value) => {
																					field.onChange(value);

																					const rolledTons = watch(
																						"ScheduledRolledTons",
																					);

																					const newRollingHs =
																						calculateRollingHours(
																							rolledTons ?? 0,
																							value ?? 1,
																						);

																					checkValueAndPrefill({
																						actualField:
																							"ScheduledRollingHours",
																						newValue: round(
																							Number(newRollingHs),
																						),
																						filledType: "autocalculated",
																						respectActualValue: false,
																					});

																					calculateNewEnd({
																						startDate,
																						estimatedRollingHs:
																							Number(newRollingHs),
																						setValue,
																						setFieldsPrefilled,
																					});

																					setFieldsPrefilled((prev) => ({
																						...prev,
																						BudgetTPH:
																							value === (sectionValues.tph ?? 0)
																								? "prefilled"
																								: false,
																					}));
																				}}
																				onBlur={field.onBlur}
																				showDecimals
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.BudgetTPH ===
																								"prefilled"
																							? "Prefilled from Section"
																							: fieldsPrefilled.BudgetTPH ===
																									"autocalculated"
																								? "Autocalculated (Rolled Tons / Rolling Hours)"
																								: ""
																				}
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="TargetTPH"
																		control={control}
																		rules={{
																			deps: ["BudgetTPH"],
																			validate: (value) => {
																				const budget = getValues("BudgetTPH");
																				if (!value || !budget) return;

																				if (value < budget) {
																					return "Target TPH cannot be lower than Budget TPH";
																				}

																				return true;
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<NumericTextField
																				label="Target TPH"
																				value={field.value}
																				onChange={(value) => {
																					field.onChange(value);

																					setFieldsPrefilled((prev) => ({
																						...prev,
																						TargetTPH:
																							value === sectionValues.targetTPH
																								? "prefilled"
																								: false,
																					}));
																				}}
																				onBlur={field.onBlur}
																				showDecimals
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.TargetTPH ===
																								"prefilled"
																							? "Prefilled from Section"
																							: ""
																				}
																			/>
																		)}
																	/>
																</Grid2>

																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="ScheduledMillYield"
																		control={control}
																		rules={{
																			deps: [
																				"ScheduledChargedTons",
																				"ScheduledRolledTons",
																			],
																			min: {
																				value: 0,
																				message: "Must be at least 0%",
																			},
																			max: {
																				value: 100,
																				message:
																					"Must be less than or equal to 100%",
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<PercentField
																				label="Mill Yield"
																				value={field.value}
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.ScheduledMillYield ===
																								"prefilled"
																							? "Prefilled from Section"
																							: fieldsPrefilled.ScheduledMillYield ===
																									"autocalculated"
																								? "Autocalculated (Roll. Tons / Charge Tons)"
																								: ""
																				}
																				disabled
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={{ md: 3 }}>
																	<Controller
																		name="ScheduledFinishedYield"
																		control={control}
																		rules={{
																			deps: [
																				"ScheduledChargedTons",
																				"ScheduledFinishedTons",
																			],
																			min: {
																				value: 0,
																				message: "Must be at least 0%",
																			},
																			max: {
																				value: 100,
																				message:
																					"Must be less than or equal to 100%",
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<PercentField
																				label="Finished Yield"
																				value={field.value}
																				error={!!error}
																				helperText={
																					error?.message
																						? error.message
																						: fieldsPrefilled.ScheduledFinishedYield ===
																								"prefilled"
																							? "Prefilled from Section"
																							: fieldsPrefilled.ScheduledFinishedYield ===
																									"autocalculated"
																								? "Autocalculated (Fin. Tons / Charge Tons)"
																								: ""
																				}
																				disabled
																			/>
																		)}
																	/>
																</Grid2>
															</Grid2>
														</Grid2>
														<Grid2
															size={{ md: 2.4 }}
															sx={{ display: "flex", flexDirection: "column" }}
														>
															<Box
																sx={{
																	border: 1,
																	borderColor: "divider",
																	borderRadius: 1,
																	px: 2,
																	py: 1,
																	display: "flex",
																	flexDirection: "column",
																	justifyContent: "center",
																	height: "100%",
																}}
															>
																<Controller
																	name="ScheduledRollChangeHours"
																	control={control}
																	rules={{
																		validate: (value) => {
																			if (value == null) return true;
																			if (!startDate || !endDate) return true;
																			const eventHours = moment(endDate).diff(
																				moment(startDate),
																				"hours",
																				true,
																			);
																			if (value > eventHours)
																				return "Exceeds event duration";
																			return true;
																		},
																	}}
																	render={({
																		field,
																		fieldState: { error: fieldError },
																	}) => (
																		<HoursMinutesTextField
																			label="Roll Change Time"
																			value={field.value}
																			onChange={(value) => {
																				field.onChange(
																					value === 0 ? null : value,
																				);
																				setFieldsPrefilled((prev) => ({
																					...prev,
																					ScheduledRollChangeHours: false,
																				}));
																				calculateNewEnd({
																					startDate,
																					estimatedRollingHs:
																						getValues(
																							"ScheduledRollingHours",
																						) ?? 0,
																					setValue,
																					setFieldsPrefilled,
																				});
																			}}
																			error={!!fieldError}
																			helperText={fieldError?.message ?? ""}
																		/>
																	)}
																/>
																<Box
																	sx={{
																		display: "flex",
																		justifyContent: "space-between",
																		mt: 0.5,
																	}}
																>
																	<Box sx={{ textAlign: "center" }}>
																		<Typography
																			variant="caption"
																			color="text.disabled"
																			display="block"
																			sx={{
																				fontSize: "0.6rem",
																				lineHeight: 1.2,
																			}}
																		>
																			Min
																		</Typography>
																		<Typography
																			variant="body2"
																			color="text.disabled"
																			sx={{ fontSize: "0.7rem" }}
																		>
																			{toHumanDuration(
																				rollChangeStats?.HistoricalMin,
																			) || "—"}
																		</Typography>
																	</Box>
																	<Box sx={{ textAlign: "center" }}>
																		<Typography
																			variant="caption"
																			color="text.disabled"
																			display="block"
																			sx={{
																				fontSize: "0.6rem",
																				lineHeight: 1.2,
																			}}
																		>
																			Max
																		</Typography>
																		<Typography
																			variant="body2"
																			color="text.disabled"
																			sx={{ fontSize: "0.7rem" }}
																		>
																			{toHumanDuration(
																				rollChangeStats?.HistoricalMax,
																			) || "—"}
																		</Typography>
																	</Box>
																	<Box sx={{ textAlign: "center" }}>
																		<Typography
																			variant="caption"
																			color="text.disabled"
																			display="block"
																			sx={{
																				fontSize: "0.6rem",
																				lineHeight: 1.2,
																			}}
																		>
																			Avg top 5
																		</Typography>
																		<Typography
																			variant="body2"
																			color="text.disabled"
																			sx={{ fontSize: "0.7rem" }}
																		>
																			{toHumanDuration(
																				rollChangeStats?.HistoricalAvgBest5,
																			) || "—"}
																		</Typography>
																	</Box>
																</Box>
															</Box>
														</Grid2>
													</Grid2>

													{isNewEventMultiMonth && (
														<>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{
																	mt: 2,
																	mb: 1,
																	display: "block",
																	fontWeight: 600,
																}}
															>
																{moment(newEventMonths[0]).format("MMMM YYYY")}
															</Typography>
															<Grid2 container spacing={2} width="100%">
																{(
																	[
																		"ScheduledChargedTons",
																		"ScheduledRolledTons",
																		"ScheduledFinishedTons",
																		"ScheduledRollingHours",
																	] as const
																).map((field) => (
																	<Grid2 key={field} size={{ md: 3 }}>
																		<NumericTextField
																			label={
																				field === "ScheduledRollingHours"
																					? "Rolling Hours"
																					: field
																							.replace("Scheduled", "")
																							.replace(/([A-Z])/g, " $1")
																							.trim()
																			}
																			value={firstMonthScheduled[field]}
																			onChange={(v) =>
																				setFirstMonthScheduled((p) => ({
																					...p,
																					[field]: v,
																				}))
																			}
																			showDecimals={
																				field !== "ScheduledRollingHours"
																			}
																			InputProps={
																				field === "ScheduledRollingHours"
																					? {
																							endAdornment: (
																								<InputAdornment position="end">
																									hs
																								</InputAdornment>
																							),
																						}
																					: undefined
																			}
																		/>
																	</Grid2>
																))}
															</Grid2>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{
																	mt: 2,
																	mb: 1,
																	display: "block",
																	fontWeight: 600,
																}}
															>
																{moment(newEventMonths[1]).format("MMMM YYYY")}
															</Typography>
															<Grid2 container spacing={2} width="100%">
																{(
																	[
																		"ScheduledChargedTons",
																		"ScheduledRolledTons",
																		"ScheduledFinishedTons",
																		"ScheduledRollingHours",
																	] as const
																).map((field) => (
																	<Grid2 key={field} size={{ md: 3 }}>
																		<NumericTextField
																			label={
																				field === "ScheduledRollingHours"
																					? "Rolling Hours"
																					: field
																							.replace("Scheduled", "")
																							.replace(/([A-Z])/g, " $1")
																							.trim()
																			}
																			value={secondMonthScheduled[field]}
																			onChange={(v) =>
																				setSecondMonthScheduled((p) => ({
																					...p,
																					[field]: v,
																				}))
																			}
																			showDecimals={
																				field !== "ScheduledRollingHours"
																			}
																			InputProps={
																				field === "ScheduledRollingHours"
																					? {
																							endAdornment: (
																								<InputAdornment position="end">
																									hs
																								</InputAdornment>
																							),
																						}
																					: undefined
																			}
																		/>
																	</Grid2>
																))}
															</Grid2>
														</>
													)}
												</AccordionDetails>
											</Accordion>
										</Grid2>
										<Grid2 size={{ md: 12 }}>
											<Accordion>
												<AccordionSummary expandIcon={<ExpandMore />} disabled>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															width: "100%",
														}}
													>
														<Typography
															variant="h6"
															color="textSecondary"
															fontSize={14}
														>
															Actual
														</Typography>
														<Typography
															variant="caption"
															color="textSecondary"
															sx={{
																flexGrow: 1,
																textAlign: "center",
																marginRight: "24px",
															}}
														>
															Update this section after you create the event
														</Typography>
													</Box>
												</AccordionSummary>
												<AccordionDetails>
													<Grid2 container spacing={2}>
														<Grid2 size={{ md: 4 }}>
															<Controller
																name="ActualChargedTons"
																control={control}
																render={({ field }) => (
																	<NumericTextField
																		label="Charged Tons"
																		value={field.value}
																		onChange={field.onChange}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 4 }}>
															<Controller
																name="ActualRolledTons"
																control={control}
																render={({ field }) => (
																	<NumericTextField
																		label="Rolled Tons"
																		value={field.value}
																		onChange={field.onChange}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 4 }}>
															<Controller
																name="ActualFinishedTons"
																control={control}
																render={({ field }) => (
																	<NumericTextField
																		label="Finished Tons"
																		value={field.value}
																		onChange={field.onChange}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ActualTPH"
																control={control}
																render={({ field }) => (
																	<TextField
																		label="TPH"
																		variant="outlined"
																		size="small"
																		fullWidth
																		type="number"
																		value={field.value || ""}
																		onChange={(e) =>
																			field.onChange(Number(e.target.value))
																		}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ActualRollingHours"
																control={control}
																render={({ field }) => (
																	<TextField
																		label="Rolling Hours"
																		variant="outlined"
																		size="small"
																		fullWidth
																		type="number"
																		value={field.value || ""}
																		onChange={(e) =>
																			field.onChange(Number(e.target.value))
																		}
																		InputProps={{
																			endAdornment: (
																				<InputAdornment position="end">
																					hs
																				</InputAdornment>
																			),
																		}}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ActualMillYield"
																control={control}
																rules={{
																	min: {
																		value: 0,
																		message: "Must be at least 0%",
																	},
																	max: {
																		value: 100,
																		message:
																			"Must be less than or equal to 100%",
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<TextField
																		label="Mill Yield"
																		variant="outlined"
																		size="small"
																		fullWidth
																		type="text"
																		value={
																			field.value !== null &&
																			field.value !== undefined
																				? field.value.toFixed(2)
																				: ""
																		}
																		onChange={(e) => {
																			const value = parseFloat(
																				e.target.value.replace(/,/g, ""),
																			);
																			field.onChange(
																				!isNaN(value) ? value : null,
																			);
																		}}
																		onBlur={(e) => {
																			const value = parseFloat(
																				e.target.value.replace(/,/g, ""),
																			);
																			field.onChange(
																				!isNaN(value) ? value : null,
																			);
																		}}
																		error={!!error}
																		helperText={error?.message}
																		InputProps={{
																			endAdornment: (
																				<InputAdornment position="end">
																					%
																				</InputAdornment>
																			),
																		}}
																		inputProps={{
																			step: 0.01,
																			min: 0,
																			max: 100,
																		}}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ActualFinishedYield"
																control={control}
																rules={{
																	min: {
																		value: 0,
																		message: "Must be at least 0%",
																	},
																	max: {
																		value: 100,
																		message:
																			"Must be less than or equal to 100%",
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<TextField
																		label="Finished Yield"
																		variant="outlined"
																		size="small"
																		fullWidth
																		type="text"
																		value={
																			field.value !== null &&
																			field.value !== undefined
																				? field.value.toFixed(2)
																				: ""
																		}
																		onChange={(e) => {
																			const value = parseFloat(
																				e.target.value.replace(/,/g, ""),
																			);
																			field.onChange(
																				!isNaN(value) ? value : null,
																			);
																		}}
																		onBlur={(e) => {
																			const value = parseFloat(
																				e.target.value.replace(/,/g, ""),
																			);
																			field.onChange(
																				!isNaN(value) ? value : null,
																			);
																		}}
																		error={!!error}
																		helperText={error?.message}
																		InputProps={{
																			endAdornment: (
																				<InputAdornment position="end">
																					%
																				</InputAdornment>
																			),
																		}}
																		inputProps={{
																			step: 0.01,
																			min: 0,
																			max: 100,
																		}}
																	/>
																)}
															/>
														</Grid2>
													</Grid2>
												</AccordionDetails>
											</Accordion>
										</Grid2>
									</>
								) : null}
							</>
						)}

						<Grid2 size={{ xs: 12 }}>
							<Card>
								<CardContent>
									<Controller
										name="EventComments"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<CommentsField
												value={field.value ?? ""}
												onChange={field.onChange}
												rows={{ minRows: 2, maxRows: 4 }}
												fieldError={error}
											/>
										)}
									/>
								</CardContent>
							</Card>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions
					sx={{
						p: 2,
					}}
				>
					<Grid2
						container
						size={12}
						justifyContent="flex-end"
						spacing={1}
						sx={{ width: "100%" }}
					>
						<Grid2
							container
							size={{ xs: 12, md: 6 }}
							spacing={1}
							justifyContent="flex-start"
							alignItems="center"
						>
							<Grid2 size={{ xs: 6 }}>
								<Controller
									name="IsDraft"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													color="primary"
													onChange={field.onChange}
												/>
											}
											label="Is Visible"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
						<Grid2
							container
							size={{ xs: 12, md: 6 }}
							spacing={1}
							justifyContent="center"
							alignItems="center"
						>
							<Grid2 size={{ xs: 6 }}>
								<Button
									variant="contained"
									color="inherit"
									fullWidth
									onClick={() => {
										handleClose(false);
									}}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<ButtonWithLoading
									isLoading={newEvent.isPending || newProductionEvent.isPending}
									variant="contained"
									fullWidth
									color="primary"
									onClick={() => {
										onSubmit(getValues());
									}}
									data-test-id="create-event-button"
								>
									Create
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<Dialog
				maxWidth="xs"
				fullWidth
				open={sectionDefaultsDiffs.length > 0}
				data-test-id="update-section-defaults-modal"
			>
				<DialogTitle>Update Section Defaults?</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" gutterBottom>
						The following values differ from section &quot;{sectionValues.name}
						&quot; defaults:
					</Typography>
					<Box
						component="table"
						sx={{ width: "100%", mt: 1, borderCollapse: "collapse" }}
					>
						<thead>
							<tr>
								<Box
									component="th"
									sx={{
										textAlign: "left",
										pb: 0.5,
										pr: 2,
										fontSize: "0.8rem",
										color: "text.secondary",
									}}
								>
									Field
								</Box>
								<Box
									component="th"
									sx={{
										textAlign: "right",
										pb: 0.5,
										pr: 2,
										fontSize: "0.8rem",
										color: "text.secondary",
									}}
								>
									Current
								</Box>
								<Box
									component="th"
									sx={{
										textAlign: "right",
										pb: 0.5,
										fontSize: "0.8rem",
										color: "text.secondary",
									}}
								>
									New
								</Box>
							</tr>
						</thead>
						<tbody>
							{sectionDefaultsDiffs.map((diff) => (
								<tr key={diff.field}>
									<Box
										component="td"
										sx={{ py: 0.5, pr: 2, fontSize: "0.85rem" }}
									>
										{diff.field}
									</Box>
									<Box
										component="td"
										sx={{
											py: 0.5,
											pr: 2,
											textAlign: "right",
											fontSize: "0.85rem",
											color: "text.secondary",
										}}
									>
										{diff.from}
									</Box>
									<Box
										component="td"
										sx={{
											py: 0.5,
											textAlign: "right",
											fontSize: "0.85rem",
											fontWeight: 600,
										}}
									>
										{diff.to}
									</Box>
								</tr>
							))}
						</tbody>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setSectionDefaultsDiffs([]);
							onSubmit(getValues(), false);
						}}
					>
						No
					</Button>
					<Button
						onClick={() => {
							setSectionDefaultsDiffs([]);
							onSubmit(getValues(), true);
						}}
						data-test-id="confirm-section-defaults-button"
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
